'use client'

import { useEffect, useRef, useState } from 'react'
import {
  LuArrowLeft,
  LuChevronDown,
  LuChevronRight,
  LuClipboardPaste,
  LuCopy,
  LuFileText,
  LuImport,
  LuSparkles,
  LuUpload,
} from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import ErrorToast from '@/core/components/error-toast'
import { Checkbox } from '@/core/components/form/checkbox'
import { Combobox } from '@/core/components/form/combobox'
import { FileInput } from '@/core/components/form/file-input'
import { Textarea } from '@/core/components/form/textarea'
import { toast } from '@/core/lib/toast'
import { copyToClipboard } from '@/core/utils/clipboard'
import { STUDIO_IMPORT_GUIDE } from '@/features/studio-renew/lib/studio-import-guide'
import {
  IMPORT_FIELDS,
  IMPORT_LABEL,
  parseStudioImport,
} from '@/features/studio-renew/lib/studio-import'
import { importEntity } from '@/features/studio-renew/services/studio-import.service'
import ServerImportReview, {
  applyImportedResult,
} from '@/features/studio-renew/components/server-import-review'
import ImportDialogueEditor from '@/features/studio-renew/components/import-dialogue-editor'
import {
  bundleToBlocks,
  choicesToBundle,
  hasBundleContent,
  selectedRootCandidate,
} from '@/features/studio-renew/lib/server-import-bundle'
import {
  clearImportRuns,
  readImportRun,
  restoreImportRun,
  startImportAnalysis,
} from '@/features/studio-renew/services/import-run.service'
import {
  importAnalysisSchema,
  importConfirmedSchema,
} from '@/features/studio-renew/schemas/import-result.schema'
import { api } from '@/features/veluvit/services/api'
import { useRenewStore } from '@/features/studio-renew/store'
import { ensureStudioCollections, flushAll } from '@/features/studio-renew/sync'
import { SheetHead } from '@/features/veluvit/components/overlay/layer-parts'
import { GemAmount } from '@/features/veluvit/components/ui/currency-icon'
import { IMPORT_MAX_CHARS } from '@/features/veluvit/services/creator-ai.service'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { ImportBlock } from '@/features/studio-renew/lib/studio-import'
import type {
  ImportChoice,
  ImportTarget,
} from '@/features/studio-renew/services/studio-import.service'
import type { EditorKind } from '@/features/studio-renew/steps'
import type {
  ImportAnalysis,
  ImportConfirmed,
} from '@/features/studio-renew/schemas/import-result.schema'
import type {
  ImportRun,
  ImportRunScope,
} from '@/features/studio-renew/services/import-run.service'
import type {
  Operation,
  OperationOptions,
} from '@/features/veluvit/services/api'

type Props = { target: ImportTarget }
const KINDS: EditorKind[] = ['work', 'character', 'world', 'status', 'command']
function withRequiredFields(
  block: ImportChoice['values'],
  kind: ImportChoice['kind']
) {
  const values = { ...block }
  let required = ['name']
  if (kind === 'command') required = ['key']
  if (kind === 'start' && block.dialogue === undefined)
    required = ['name', 'greeting']
  for (const key of required) values[key] ??= ''
  return values
}

function reviewChoices(
  blocks: ImportBlock[],
  target: ImportTarget
): ImportChoice[] {
  const original = importEntity(target)
  let rootFound = false
  const choices = blocks
    .map((block): ImportChoice => {
      const root = block.kind === target.kind && !rootFound
      if (root) rootFound = true
      const values =
        !root || !target.id
          ? withRequiredFields(block.values, block.kind)
          : block.values
      const fields = Object.entries(values)
        .filter(([key]) => Object.hasOwn(IMPORT_FIELDS[block.kind], key))
        .map(([key, value]) => {
          const old: unknown =
            root && original
              ? Reflect.get(
                  original,
                  block.kind === 'work' && key === 'leadName' ? 'lead' : key
                )
              : undefined
          return {
            key,
            label: IMPORT_FIELDS[block.kind][key],
            value,
            selected:
              old == null || old === '' || (Array.isArray(old) && !old.length),
          }
        })
      return {
        ...block,
        values,
        root,
        fields,
        selected: root && fields.some((field) => field.selected),
      }
    })
    .filter((block) => block.fields.length)
  if (!rootFound && !target.id && choices.length) {
    const values = withRequiredFields({}, target.kind)
    choices.unshift({
      key: `${target.kind}:root`,
      kind: target.kind,
      root: true,
      selected: true,
      values,
      fields: Object.entries(values).map(([key, value]) => ({
        key,
        label: IMPORT_FIELDS[target.kind][key],
        value,
        selected: true,
      })),
    })
  }
  return choices
}

/** 모든 진입점에서 동일한 방식 선택과 전체 원고를 제공하고, 적용 대상은 검토 단계에서 정한다. */
export default function ImportPanel({ target }: Props) {
  const owner = useRenewStore((state) => state.owner)
  const identity = useSessionStore((state) => state.identity)
  const [method, setMethod] = useState<'file' | 'text' | 'ai' | null>(null)
  const [destination, setDestination] = useState(
    target.id ? 'current' : target.kind
  )
  const applyTarget: ImportTarget =
    destination === 'current' ? target : { kind: destination as EditorKind }
  const [text, setText] = useState('')
  const [fileName, setFileName] = useState('')
  const [choices, setChoices] = useState<ImportChoice[] | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const [fingerprint, setFingerprint] = useState('')
  const [busy, setBusy] = useState(false)
  // 패널을 처음 열 때 이어서 진행할 이식 기록을 한 번 읽는다.
  const [previous] = useState<{ run: ImportRun | null; error: string }>(() => {
    try {
      return {
        run: readImportRun({
          owner: owner ?? '',
          target: { kind: target.kind, id: target.id },
        }),
        error: '',
      }
    } catch {
      return {
        run: null,
        error:
          '이전 이식 기록을 읽지 못했어요. 이 기기의 저장 공간을 확인해 주세요.',
      }
    }
  })
  const [error, setError] = useState(previous.error)
  const [comparison, setComparison] = useState<{
    destination: ImportTarget
    analysisId?: string
    analysis?: ImportAnalysis
    completed?: ImportConfirmed
    preserved?: string[]
  } | null>(null)
  const [restore, setRestore] = useState(previous.run)
  const [operation, setOperation] = useState<{
    id: string
    status: string
    cost: number
  } | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const controller = useRef<AbortController | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const textRef = useRef<HTMLTextAreaElement>(null)
  const readerRef = useRef<FileReader | null>(null)
  const active = useRef(false)
  const lock = useRef(false)
  const [initial] = useState(() => ({
    owner,
    identity,
    kind: target.kind,
    id: target.id,
  }))
  const current = () =>
    active.current &&
    initial.owner === useRenewStore.getState().owner &&
    initial.identity === useSessionStore.getState().identity
  const scope: ImportRunScope = { owner: owner ?? '', target }
  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
      readerRef.current?.abort()
      controller.current?.abort()
    }
  }, [])
  const options = (): OperationOptions => {
    controller.current?.abort()
    controller.current = new AbortController()
    return {
      signal: controller.current.signal,
      onOperation: (value: Operation) => {
        if (current())
          setOperation({
            id: value.id,
            status: value.status,
            cost: typeof value.cost === 'number' ? value.cost : 0,
          })
      },
    }
  }
  const restorePrevious = async () => {
    if (lock.current || !restore || !current()) return
    lock.current = true
    setBusy(true)
    setError('')
    try {
      const output = await restoreImportRun(scope, restore, options())
      if (!current()) return
      setText(restore.text)
      setDestination(
        restore.destination.id ? 'current' : restore.destination.kind
      )
      if (restore.phase === 'confirm') {
        const result = importConfirmedSchema.parse(output.result)
        const preserved = await applyImportedResult(result)
        if (!current()) return
        setComparison({
          destination: restore.destination,
          completed: result,
          preserved,
        })
        try {
          clearImportRuns(scope)
        } catch {
          /* 완료 영수증을 보존한다. */
        }
      } else {
        const result = importAnalysisSchema.parse(output.result)
        if (result.wallet) useSessionStore.getState().applyWallet(result.wallet)
        if (restore.phase === 'compare')
          setComparison({
            destination: restore.destination,
            analysisId: output.operationId,
            analysis: result,
          })
        else {
          setMethod('ai')
          setChoices(
            reviewChoices(bundleToBlocks(result.bundle), restore.destination)
          )
          setWarnings(result.warnings)
          setFingerprint(
            JSON.stringify(importEntity(restore.destination)) ?? ''
          )
        }
      }
      setRestore(null)
    } catch (cause) {
      if (current())
        setError(
          cause instanceof Error
            ? cause.message
            : '이전 작업을 확인하지 못했어요.'
        )
    } finally {
      lock.current = false
      if (current()) {
        setBusy(false)
        setCancelling(false)
      }
    }
  }

  const acceptText = (value: string) => {
    if (value.length > IMPORT_MAX_CHARS) {
      setError('50,000자가 넘어요. 원문을 나누어 가져와 주세요.')
      return false
    }
    setText(value)
    setError('')
    return true
  }
  const copyGuide = async () => {
    const ok = await copyToClipboard(STUDIO_IMPORT_GUIDE)
    if (current())
      toast.show(
        ok ? '전체 작성 가이드를 복사했어요' : '작성 가이드를 복사하지 못했어요'
      )
  }
  const paste = async () => {
    try {
      const value = await navigator.clipboard.readText()
      if (current() && acceptText(value)) {
        setFileName('')
        textRef.current?.focus()
      }
    } catch {
      if (current()) setError('클립보드를 읽지 못했어요. 직접 붙여넣어 주세요.')
    }
  }
  const readFile = (file?: File) => {
    if (!file) return
    if (file.size > 1024 * 1024 || !/\.(txt|md)$/i.test(file.name)) {
      setError('1MB 이하 TXT·MD 파일을 선택해 주세요.')
      return
    }
    readerRef.current?.abort()
    const reader = new FileReader()
    readerRef.current = reader
    reader.onload = () => {
      if (
        current() &&
        readerRef.current === reader &&
        acceptText(String(reader.result))
      )
        setFileName(file.name)
    }
    reader.onerror = () => {
      if (current() && readerRef.current === reader)
        setError('파일을 읽지 못했어요.')
    }
    reader.readAsText(file)
  }
  const analyze = async () => {
    if (lock.current || !current() || !text.trim()) return
    lock.current = true
    setBusy(true)
    setError('')
    const original = importEntity(applyTarget)
    const version = JSON.stringify(original) ?? ''
    try {
      if (applyTarget.id && !original)
        throw new Error(
          '가져올 원본을 찾지 못했어요. 목록에서 다시 열어 주세요.'
        )
      let parsed: { blocks: ImportBlock[]; warnings: string[] }
      if (method === 'ai') {
        const output = await startImportAnalysis(
          scope,
          'ai',
          {
            targetKind: target.kind,
            text,
            method: 'ai',
            source: 'studio-renew',
          },
          applyTarget,
          options()
        )
        if (!current()) return
        if (output.result.wallet)
          useSessionStore.getState().applyWallet(output.result.wallet)
        parsed = {
          blocks: bundleToBlocks(output.result.bundle),
          warnings: [
            ...output.result.warnings,
            ...output.result.unclassified.map(
              (row) =>
                `미분류 원문: ${typeof row === 'string' ? row : JSON.stringify(row)}`
            ),
          ],
        }
      } else parsed = await parseStudioImport(text, target.kind)
      await ensureStudioCollections([
        'character',
        'world',
        'status',
        'command',
        'chat',
      ])
      if (!current()) return
      if ((JSON.stringify(importEntity(applyTarget)) ?? '') !== version)
        throw new Error('원본이 변경됐어요. 다시 확인해 주세요.')
      const next = reviewChoices(parsed.blocks, applyTarget)
      if (!next.length)
        throw new Error(
          '가져올 내용을 찾지 못했어요. 작성 가이드의 제목을 확인해 주세요.'
        )
      setChoices(next)
      setWarnings(parsed.warnings)
      setFingerprint(version)
      setRestore(null)
    } catch (cause) {
      if (current()) {
        setError(
          cause instanceof Error ? cause.message : '원문을 분류하지 못했어요.'
        )
        try {
          setRestore(readImportRun(scope))
        } catch {
          /* 기존 기록은 보존한다. */
        }
      }
    } finally {
      lock.current = false
      if (current()) {
        setBusy(false)
        setCancelling(false)
      }
    }
  }
  const changeDestination = (value: string) => {
    if (
      busy ||
      !choices ||
      (value === 'current' && !target.id) ||
      (value !== 'current' && !KINDS.includes(value as EditorKind))
    )
      return
    const next: ImportTarget =
      value === 'current' ? target : { kind: value as EditorKind }
    // 편집한 원고 값은 유지하고, 기존 값 보호를 위해 항목 선택만 다시 계산한다.
    const blocks = choices
      .filter(
        (choice) =>
          !choice.key.endsWith(':root') ||
          choice.fields.some((field) => field.value.trim())
      )
      .map((choice) => ({
        key: choice.key,
        kind: choice.kind,
        values: Object.fromEntries(
          choice.fields.map((field) => [field.key, field.value])
        ),
      }))
    setDestination(value)
    setChoices(reviewChoices(blocks, next))
    setFingerprint(JSON.stringify(importEntity(next)) ?? '')
    setError('')
  }
  const apply = async () => {
    if (lock.current || !current() || !choices) return
    lock.current = true
    setBusy(true)
    setError('')
    try {
      await flushAll()
      if (
        applyTarget.id &&
        (JSON.stringify(importEntity(applyTarget)) ?? '') !== fingerprint
      )
        throw new Error(
          '원본이 변경됐어요. 현재 내용을 확인한 뒤 다시 분석해 주세요.'
        )
      const bundle = choicesToBundle(choices)
      if (!hasBundleContent(bundle))
        throw new Error('비어 있지 않은 내용을 선택해 주세요.')
      const destination = {
        ...applyTarget,
        rootCandidate: selectedRootCandidate(choices),
        baseRevision: importEntity(applyTarget)?.revision,
      }
      const output = await startImportAnalysis(
        scope,
        'compare',
        {
          targetKind: applyTarget.kind,
          text,
          method: 'structured',
          source: 'author-review',
          bundle,
        },
        destination,
        options()
      )
      if (current()) {
        setComparison({
          destination,
          analysisId: output.analysisId,
          analysis: output.result,
        })
        setRestore(null)
      }
    } catch (cause) {
      if (current()) {
        setError(
          cause instanceof Error
            ? cause.message
            : '기존 재료를 비교하지 못했어요.'
        )
        try {
          setRestore(readImportRun(scope))
        } catch {
          /* 기존 기록은 보존한다. */
        }
      }
    } finally {
      lock.current = false
      if (current()) {
        setBusy(false)
        setCancelling(false)
      }
    }
  }
  const editGroup = (
    key: string,
    update: (choice: ImportChoice) => ImportChoice
  ) =>
    setChoices(
      (value) =>
        value?.map((choice) =>
          choice.key === key ? update(choice) : choice
        ) ?? null
    )
  const selectedCount =
    choices?.filter(
      (choice) =>
        choice.selected && choice.fields.some((field) => field.selected)
    ).length ?? 0
  let title = '어떻게 이식하시겠어요?'
  if (method)
    title = {
      file: 'TXT 원고 가져오기',
      text: '텍스트 붙여넣기',
      ai: 'AI로 정리하기',
    }[method]
  if (choices) title = '가져올 내용을 확인해 주세요'
  const validScope =
    owner === initial.owner &&
    identity === initial.identity &&
    target.kind === initial.kind &&
    target.id === initial.id
  if (!validScope)
    return (
      <>
        <SheetHead title="이식하기" />
        <p role="alert" className="text-c14 text-t3">
          계정이나 편집 대상이 바뀌었어요. 다시 열어 주세요.
        </p>
      </>
    )

  if (comparison)
    return (
      <ServerImportReview
        scope={scope}
        text={text}
        destination={comparison.destination}
        analysisId={comparison.analysisId}
        analysis={comparison.analysis}
        initialResult={comparison.completed}
        initialPreserved={comparison.preserved}
        onBack={() => setComparison(null)}
      />
    )

  return (
    <>
      <SheetHead title={title}>
        {(choices || method) && (
          <Button
            variant="ghost"
            size="icon-sm"
            className="order-first -ml-2 flex-none"
            aria-label={
              choices ? '원문으로 돌아가기' : '이식 방법 선택으로 돌아가기'
            }
            disabled={busy}
            onClick={() => {
              if (choices) setChoices(null)
              else setMethod(null)
              setError('')
            }}
          >
            <LuArrowLeft aria-hidden="true" className="size-4" />
          </Button>
        )}
      </SheetHead>
      {restore && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-2 border-b border-border pb-4 text-c13 text-t3 over10:text-c14">
          <p>이전에 시작한 이식 작업을 이어서 확인할 수 있어요.</p>
          <Button
            size="sm"
            loading={busy}
            onClick={() => void restorePrevious()}
          >
            이어서 확인
          </Button>
        </div>
      )}
      {!choices && !method && (
        <div className="space-y-5">
          <p className="max-w-[62ch] text-c14 leading-6 text-t3">
            TXT 파일이나 원문을 가져와 필요한 내용을 골라 이식해요.
            캐릭터·세계관·명령어·상태창·작품을 모두 가져올 수 있어요.
          </p>
          <Button
            variant="ghost"
            shape="field"
            className="min-h-18! w-full justify-start! gap-3! border border-border bg-foreground/[0.02] px-4! py-3.5! text-left"
            onClick={() => void copyGuide()}
          >
            <LuCopy
              aria-hidden="true"
              className="size-5 flex-none text-point"
            />
            <span className="min-w-0 flex-1">
              <span className="block text-c15 font-semibold">
                작성 가이드 복사
              </span>
              <span className="mt-1 block whitespace-normal text-c13 font-normal leading-5 text-t3 over10:text-c14">
                다른 AI에 전달할 전체 작성 형식을 복사해요.
              </span>
            </span>
            <LuChevronRight
              aria-hidden="true"
              className="size-4 flex-none text-t3"
            />
          </Button>
          <div className="grid gap-3 over6:grid-cols-2">
            <Button
              variant="ghost"
              shape="field"
              className="min-h-32! w-full items-start! justify-start! gap-3! border border-border bg-foreground/[0.02] px-4! py-5! text-left whitespace-normal break-keep"
              onClick={() => {
                setMethod('file')
                setError('')
                fileRef.current?.click()
              }}
            >
              <LuFileText
                aria-hidden="true"
                className="mt-0.5 size-5 flex-none text-point"
              />
              <span className="min-w-0">
                <span className="block text-c15 font-semibold">
                  TXT 파일을 올릴게요
                </span>
                <span className="mt-2 block whitespace-normal text-c13 font-normal leading-6 text-t3 over10:text-c14">
                  저장해 둔 원고를 올리고, 내용을 확인한 뒤 가져와요.
                </span>
              </span>
            </Button>
            <Button
              variant="ghost"
              shape="field"
              className="min-h-32! w-full items-start! justify-start! gap-3! border border-border bg-foreground/[0.02] px-4! py-5! text-left whitespace-normal break-keep"
              onClick={() => {
                setMethod('text')
                setError('')
              }}
            >
              <LuClipboardPaste
                aria-hidden="true"
                className="mt-0.5 size-5 flex-none text-point"
              />
              <span className="min-w-0">
                <span className="block text-c15 font-semibold">
                  통째로 텍스트를 붙여넣을게요
                </span>
                <span className="mt-2 block whitespace-normal text-c13 font-normal leading-6 text-t3 over10:text-c14">
                  설정과 작품 정보가 섞인 원문을 한 번에 붙여넣어요.
                </span>
              </span>
            </Button>
            <Button
              variant="ghost"
              shape="field"
              className="min-h-24! w-full items-start! justify-start! gap-3! border border-border bg-foreground/[0.02] px-4! py-4! text-left whitespace-normal break-keep over6:col-span-2"
              onClick={() => {
                setMethod('ai')
                setError('')
              }}
            >
              <LuSparkles
                aria-hidden="true"
                className="mt-0.5 size-5 flex-none text-point"
              />
              <span className="min-w-0">
                <span className="block text-c15 font-semibold">
                  AI가 알아서 정리해 줘
                </span>
                <span className="mt-1.5 block whitespace-normal text-c13 font-normal leading-6 text-t3 over10:text-c14">
                  자유로운 원문을 우리 스튜디오 형식에 맞춰 정리해요.
                </span>
              </span>
            </Button>
          </div>
        </div>
      )}
      {!choices && method && (
        <div className="space-y-4">
          <p className="text-c14 leading-6 text-t3">
            원문을 넣고 내용을 확인해 주세요. 가져올 재료와 적용할 대상은 다음
            단계에서 선택해요.
          </p>
          {fileName && (
            <p className="flex items-center gap-1.5 truncate text-c13 text-t3 over10:text-c14">
              <LuFileText aria-hidden="true" className="size-4 flex-none" />
              {fileName}
            </p>
          )}
          <Textarea
            ref={textRef}
            rows={9}
            value={text}
            disabled={busy}
            aria-label="이식할 원문"
            placeholder="원문을 붙여넣어 주세요. 작성 가이드로 정리한 TXT도 그대로 가져올 수 있어요."
            onChange={(event) => acceptText(event.target.value)}
            classNames={{ textarea: 'min-h-48! max-h-[40dvh] leading-6' }}
          />
          <p className="text-right text-c12 tabular-nums text-t3 over10:text-c14">
            {text.length.toLocaleString()} / 50,000
          </p>
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              <LuUpload aria-hidden="true" className="size-4" />
              파일 선택
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => void paste()}
            >
              <LuClipboardPaste aria-hidden="true" className="size-4" />
              붙여넣기
            </Button>
            <span className="flex-1" />
            <Button
              size="sm"
              loading={busy}
              disabled={!text.trim()}
              onClick={() => void analyze()}
            >
              {method === 'ai' ? 'AI로 정리하기' : '가져올 내용 확인'}
            </Button>
          </div>
        </div>
      )}
      {choices && (
        <div className="space-y-4">
          <p className="text-c14 leading-6 text-t3">
            {applyTarget.id
              ? '기존 값은 체크한 항목만 바뀌어요.'
              : '필요한 재료와 항목을 선택해 주세요.'}
            {applyTarget.kind === 'work' &&
              ' 기존 재료와 비교한 뒤 새로 만들거나 재사용할 수 있어요. 기존 출연진과 시작 장면은 유지해요.'}
          </p>
          <Combobox
            label="적용할 대상"
            value={destination}
            onChange={changeDestination}
            disabled={busy}
            options={[
              {
                value: 'current',
                label: '현재 편집 중인 항목',
                disabled: !target.id,
              },
              ...KINDS.map((kind) => ({
                value: kind,
                label: `새 ${IMPORT_LABEL[kind]}${kind === 'work' ? ' · 전체 구성 함께 가져오기' : ''}`,
              })),
            ]}
          />
          <p className="text-c13 leading-5 text-t3 over10:text-c14">
            모든 재료를 함께 이식하려면 새 작품을 선택해요. 적용 대상을 바꾸면
            항목 선택을 다시 확인해 주세요.
          </p>
          <div className="divide-y divide-border">
            {choices.map((choice) => (
              <div
                key={choice.key}
                className="flex items-start gap-3 py-4 first:pt-0"
              >
                <Checkbox
                  classNames={{ container: 'pt-2' }}
                  aria-label={`${IMPORT_LABEL[choice.kind]} ${choice.values.name || choice.values.title || ''} 가져오기`}
                  checked={choice.selected}
                  disabled={busy}
                  onChange={(event) =>
                    editGroup(choice.key, (value) => ({
                      ...value,
                      selected: event.target.checked,
                      fields:
                        event.target.checked &&
                        !value.fields.some((field) => field.selected)
                          ? value.fields.map((field) => ({
                              ...field,
                              selected: true,
                            }))
                          : value.fields,
                    }))
                  }
                />
                <details open={choice.selected} className="min-w-0 flex-1">
                  <summary className="flex min-h-9 cursor-pointer list-none items-center gap-3 [&::-webkit-details-marker]:hidden">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-c15 font-semibold">
                        {choice.values.name ||
                          choice.values.title ||
                          IMPORT_LABEL[choice.kind]}
                      </span>
                      <span className="text-c12 text-t3 over10:text-c14">
                        {choice.root
                          ? `${IMPORT_LABEL[choice.kind]} 정보에 반영`
                          : choice.kind === 'start'
                            ? '작품에 시작 장면 추가'
                            : `${IMPORT_LABEL[choice.kind]} 비교하고 가져오기`}
                      </span>
                    </span>
                    <LuChevronDown
                      aria-hidden="true"
                      className="size-4 text-t3"
                    />
                  </summary>
                  <div className="mt-4 grid gap-4">
                    {choice.fields.map((field) => (
                      <div key={field.key} className="space-y-2">
                        <Checkbox
                          label={
                            field.label +
                            (field.key === 'keywords' &&
                            choice.root &&
                            applyTarget.id
                              ? ' · 기존 항목에 추가'
                              : '')
                          }
                          checked={field.selected}
                          disabled={busy || !choice.selected}
                          onChange={(event) =>
                            editGroup(choice.key, (value) => ({
                              ...value,
                              fields: value.fields.map((row) =>
                                row.key === field.key
                                  ? { ...row, selected: event.target.checked }
                                  : row
                              ),
                            }))
                          }
                        />
                        {field.key === 'dialogue' ? (
                          <ImportDialogueEditor
                            value={field.value}
                            disabled={
                              busy || !choice.selected || !field.selected
                            }
                            onChange={(text) =>
                              editGroup(choice.key, (value) => ({
                                ...value,
                                values: { ...value.values, [field.key]: text },
                                fields: value.fields.map((row) =>
                                  row.key === field.key
                                    ? { ...row, value: text }
                                    : row
                                ),
                              }))
                            }
                          />
                        ) : (
                          <Textarea
                            aria-label={`${IMPORT_LABEL[choice.kind]} ${field.label} 가져올 내용`}
                            rows={
                              [
                                'name',
                                'tagline',
                                'title',
                                'key',
                                'genre',
                                'target',
                                'leadName',
                                'speaker',
                                'place',
                              ].includes(field.key)
                                ? 1
                                : 3
                            }
                            value={field.value}
                            disabled={
                              busy || !choice.selected || !field.selected
                            }
                            onChange={(event) =>
                              editGroup(choice.key, (value) => ({
                                ...value,
                                values: {
                                  ...value.values,
                                  [field.key]: event.target.value,
                                },
                                fields: value.fields.map((row) =>
                                  row.key === field.key
                                    ? { ...row, value: event.target.value }
                                    : row
                                ),
                              }))
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            ))}
          </div>
          {warnings.length > 0 && (
            <div
              className="space-y-1 text-c13 leading-6 text-t3 over10:text-c14"
              role="status"
            >
              {warnings.slice(0, 5).map((notice) => (
                <p key={notice}>{notice}</p>
              ))}
              {warnings.length > 5 && (
                <p>
                  분류 안내 {warnings.length}개 · 전체 원문을 함께 확인해
                  주세요.
                </p>
              )}
            </div>
          )}
          <details className="border-t border-border pt-3 text-c13 text-t3 over10:text-c14">
            <summary className="cursor-pointer py-1">보관한 원문</summary>
            <pre className="mt-2 max-h-52 overflow-auto whitespace-pre-wrap break-words leading-6">
              {text}
            </pre>
          </details>
          <div className="flex justify-end border-t border-border pt-4">
            <Button
              size="sm"
              loading={busy}
              disabled={!selectedCount}
              onClick={() => void apply()}
            >
              <LuImport aria-hidden="true" className="size-4" />
              기존 재료와 비교하기 · {selectedCount}
            </Button>
          </div>
        </div>
      )}
      {busy && operation && (
        <div
          role="status"
          className="mt-4 flex items-center justify-between gap-2 text-c13 text-t3 over10:text-c14"
        >
          <p>
            {operation.status === 'complete' && restore?.phase === 'confirm'
              ? '이식은 저장됐어요. 편집할 최신 내용을 불러오고 있어요…'
              : operation.status === 'queued'
                ? '분석을 준비하고 있어요…'
                : '이식 내용을 확인하고 있어요…'}
            {operation.cost > 0 && (
              <GemAmount value={operation.cost} className="ml-2 text-gem" />
            )}
          </p>
          {method === 'ai' &&
            ['queued', 'running'].includes(operation.status) && (
              <Button
                variant="ghost"
                size="sm"
                disabled={cancelling}
                onClick={() => {
                  setCancelling(true)
                  void api(
                    `/operation/${encodeURIComponent(operation.id)}/cancel/`,
                    {}
                  ).catch((cause: unknown) => {
                    if (current()) {
                      setCancelling(false)
                      setError(
                        cause instanceof Error
                          ? cause.message
                          : '취소 요청을 확인하지 못했어요.'
                      )
                    }
                  })
                }}
              >
                {cancelling ? '취소 확인 중…' : '생성 취소'}
              </Button>
            )}
        </div>
      )}
      <FileInput
        ref={fileRef}
        accept=".txt,.md,text/plain,text/markdown"
        className="hidden"
        aria-label="이식할 파일"
        onChange={(event) => {
          readFile(event.target.files?.[0])
          event.target.value = ''
        }}
      />
      <ErrorToast message={error} />
    </>
  )
}
